/**
 * Waves Autotune - Matrix channel-conflict prompt state.
 *
 * The autotune's key/scale CC numbers (55/56) are fixed, so putting the
 * item on a MIDI channel that already claims those CCs in the Matrix is a
 * takeover. We surface that whenever it happens - on add (the item is born
 * on its default channel) and on an Inspector channel change - but ONLY
 * when there is an actual conflict on the target channel.
 *
 * This lives in its own module (not the InspectorPanel) so the modal can be
 * mounted once at the App root and fire regardless of whether the item is
 * currently selected - the add path isn't a selection event.
 */

import { createSignal, type Accessor } from 'solid-js';
import { slotForCc, matrixDevices } from '../matrix/store.ts';
import { editItem, removeItem, type WavesAutotuneItem } from '../layout/store.ts';
import {
  AUTOTUNE_KEY_CC, AUTOTUNE_SCALE_CC, AUTOTUNE_STRENGTH_CC,
} from './session.ts';

/** One (channel, cc) Matrix conflict. `role` is Key/Scale; `occupants` is
 *  what the Matrix already has claimed there. */
export interface WavesChannelConflict {
  cc: number;
  role: string;
  occupants: string[];
}

/** How the item arrived on the conflicting channel. `add` = just created
 *  (Cancel removes it); `change` = Inspector channel edit (Cancel is a
 *  no-op, the channel was never applied). */
export type WavesConflictMode = 'add' | 'change';

export interface PendingWavesConflict {
  itemId: string;
  channel: number;
  mode: WavesConflictMode;
  conflicts: WavesChannelConflict[];
}

const [pending, setPending] = createSignal<PendingWavesConflict | null>(null);
/** The active conflict prompt, or null. The App-level modal reads this. */
export const pendingWavesConflict: Accessor<PendingWavesConflict | null> = pending;

/** What the autotune's fixed CCs would collide with in the Matrix on a
 *  given channel. Empty when all three are free there.
 *
 *  Strength (CC 6) joined the check 2026-09-01: it was being dispatched
 *  without ever being conflict-checked, so a channel could be reported clean
 *  and still collide on it. It matters more than the others here - CC 6 is
 *  low and far likelier to be claimed by something else than 55/56. */
export function wavesChannelConflicts(channel: number): WavesChannelConflict[] {
  const out: WavesChannelConflict[] = [];
  for (const { cc, role } of [
    { cc: AUTOTUNE_KEY_CC, role: 'Key' },
    { cc: AUTOTUNE_SCALE_CC, role: 'Scale' },
    { cc: AUTOTUNE_STRENGTH_CC, role: 'Strength' },
  ]) {
    const slot = slotForCc(channel, cc);
    if (slot && slot.claims.length > 0) {
      const occupants = slot.claims.map((c) => {
        const dev = matrixDevices().find((d) => d.id === c.deviceId);
        return `${dev?.name ?? 'Unknown device'} - ${c.controlLabel}`;
      });
      out.push({ cc, role, occupants });
    }
  }
  return out;
}

/**
 * Route a Waves Autotune onto `channel`. If the channel is conflict-free,
 * apply immediately (only meaningful for a `change` - an `add` is already
 * on its channel). If there ARE conflicts, open the takeover prompt; the
 * App-level modal commits or backs out.
 */
export function requestWavesChannel(
  item: WavesAutotuneItem,
  channel: number,
  mode: WavesConflictMode,
): void {
  const conflicts = wavesChannelConflicts(channel);
  if (conflicts.length === 0) {
    if (mode === 'change') editItem(item.id, { channel });
    return;
  }
  setPending({ itemId: item.id, channel, mode, conflicts });
}

/** Confirm the takeover: apply the channel (a `change` commits it; an `add`
 *  is already there so it's a keep) and dismiss. */
export function confirmWavesConflict(): void {
  const p = pending();
  if (!p) return;
  if (p.mode === 'change') editItem(p.itemId, { channel: p.channel });
  setPending(null);
}

/** Back out: an `add` removes the freshly-created item; a `change` just
 *  dismisses (the channel was never applied). */
export function cancelWavesConflict(): void {
  const p = pending();
  if (!p) return;
  if (p.mode === 'add') removeItem(p.itemId);
  setPending(null);
}
